"use client";

import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "./button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./select";

export interface PaginationMeta {
  totalItems: number;
  page: number;
  limit: number;
  totalPages: number;
  itemCount: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

interface DataPaginationProps {
  meta: PaginationMeta;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  pageSizeOptions?: number[];
  isLoading?: boolean;
  showPageSize?: boolean;
}

export const DataPagination = function ({
  meta,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 25, 50, 100],
  isLoading = false,
  showPageSize = true,
}: DataPaginationProps) {
  const { page, totalPages, totalItems, limit, hasPreviousPage, hasNextPage } = meta;

  const handlePrevious = () => {
    if (hasPreviousPage && !isLoading) {
      onPageChange(page - 1);
    }
  };

  const handleNext = () => {
    if (hasNextPage && !isLoading) {
      onPageChange(page + 1);
    }
  };

  const handlePageSizeChange = (value: string) => {
    if (onPageSizeChange && !isLoading) {
      onPageSizeChange(parseInt(value, 10));
      // Reset to page 1 when changing page size
      onPageChange(1);
    }
  };

  // Calculate the range of items being displayed
  const startItem = totalItems === 0 ? 0 : (page - 1) * limit + 1;
  const endItem = Math.min(page * limit, totalItems);

  if (totalItems === 0) {
    return null;
  }

  return (
    <div className="flex items-center justify-between px-2 py-4 border-t">
      <div className="flex items-center gap-4">
        <div className="text-sm text-muted-foreground">
          Showing <span className="font-medium">{startItem}</span> to{" "}
          <span className="font-medium">{endItem}</span> of{" "}
          <span className="font-medium">{totalItems}</span> items
        </div>

        {showPageSize && onPageSizeChange && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Items per page:</span>
            <Select
              value={limit.toString()}
              onValueChange={handlePageSizeChange}
              disabled={isLoading}
            >
              <SelectTrigger className="h-8 w-[70px]">
                <SelectValue placeholder={limit.toString()} />
              </SelectTrigger>
              <SelectContent>
                {pageSizeOptions.map((size) => (
                  <SelectItem key={size} value={size.toString()}>
                    {size}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      <div className="flex items-center gap-2">
        <div className="text-sm text-muted-foreground flex items-center gap-1">
          Page{" "}
          {totalPages > 1 ? (
            // Type a page number, Enter (or blur) jumps. Clamped to the range.
            <input
              type="number"
              inputMode="numeric"
              min={1}
              max={totalPages}
              key={page}
              defaultValue={page}
              disabled={isLoading}
              aria-label="Go to page"
              onKeyDown={(e) => {
                if (e.key !== "Enter") return;
                const n = Math.min(Math.max(parseInt((e.target as HTMLInputElement).value, 10) || page, 1), totalPages);
                if (n !== page) onPageChange(n);
              }}
              onBlur={(e) => {
                const n = Math.min(Math.max(parseInt(e.target.value, 10) || page, 1), totalPages);
                if (n !== page) onPageChange(n);
              }}
              className="h-7 w-10 rounded-md border border-input bg-background px-1 text-center text-sm font-medium text-foreground tabular-nums [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none focus:outline-none focus:ring-2 focus:ring-primary"
            />
          ) : (
            <span className="font-medium">{page}</span>
          )}{" "}
          of <span className="font-medium">{totalPages || 1}</span>
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="sm"
            onClick={handlePrevious}
            disabled={!hasPreviousPage || isLoading}
            className="h-8 w-8 p-0"
          >
            <ChevronLeft className="h-4 w-4" />
            <span className="sr-only">Previous page</span>
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleNext}
            disabled={!hasNextPage || isLoading}
            className="h-8 w-8 p-0"
          >
            <ChevronRight className="h-4 w-4" />
            <span className="sr-only">Next page</span>
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DataPagination;
